import React, {useState} from 'react';
import {Text, View} from 'react-native';

import CCText from '../../../Atoms/CCText';
import type {CourseCover as Cover} from '../../../Hooks/LmsCourseApi/types';
import {COVER_H, COVER_W, coachLabel, initials, photoLayout, sideToMove} from '../coverLogic';
import {F} from '../courseTheme';
import MiniBoard from './MiniBoard';

type Props = {
  cover: Cover;
  /** The AI artwork, laid underneath. Null: a plain dark ground. */
  thumbnail?: string | null;
  /** Width to draw at; the 640x360 composition is scaled to it. 0: nothing yet. */
  width: number;
};

const ACCENT = F.yellow;
const DISPLAY = 'Thunder';
const PHOTO = 64;
const SHADOW = {
  textShadowColor: 'rgba(0,0,0,0.6)',
  textShadowOffset: {width: 0, height: 4},
  textShadowRadius: 0,
};

/**
 * A course thumbnail built from the course itself: its title, a puzzle
 * position from it with the spoiler-safe task, a hook, its themes and the
 * coach — over the AI artwork. Composed here rather than baked into the
 * image, so the card and the reviewer's preview are the same picture and a
 * new crop or puzzle shows at once.
 *
 * Laid out once at 640x360 and scaled, so every size is the same design.
 */
export default function CourseCover({cover, thumbnail, width}: Props) {
  // Artwork that will not load leaves the dark ground, not a broken image.
  const [artBroken, setArtBroken] = useState(false);
  if (!width) return null;
  const art = thumbnail && !artBroken ? thumbnail : null;
  const scale = width / COVER_W;
  const height = (width * COVER_H) / COVER_W;
  const hasBoard = !!cover.fen;
  const coach = cover.coach;
  const photo = coach?.photo_url
    ? photoLayout(PHOTO, coach.photo_width, coach.photo_height, coach.crop)
    : null;

  return (
    <View style={{width, height, overflow: 'hidden', backgroundColor: '#14100D'}}>
      <View
        testID="cover-stage"
        style={{
          position: 'absolute',
          width: COVER_W,
          height: COVER_H,
          left: (width - COVER_W) / 2,
          top: (height - COVER_H) / 2,
          transform: [{scale}],
        }}>
        {art ? (
          <img
            src={art}
            alt=""
            onError={() => setArtBroken(true)}
            style={{position: 'absolute', inset: 0, width: '100%', height: '100%', objectFit: 'cover'}}
          />
        ) : null}
        {/* Darkens any artwork enough for white display type to read on it. */}
        <View
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: art ? 'rgba(12,10,8,0.55)' : 'transparent',
          }}
        />

        {hasBoard ? (
          <>
            <View
              style={{
                position: 'absolute',
                left: 34,
                top: 58,
                width: 232,
                height: 232,
                padding: 4,
                borderRadius: 8,
                backgroundColor: '#262322',
                transform: [{rotate: '-3deg'}],
              }}>
              <MiniBoard
                fen={cover.fen as string}
                orientation={sideToMove(cover.fen)}
                size={224}
                label="A puzzle from this course"
              />
              <View
                style={{
                  position: 'absolute',
                  top: -14,
                  right: -14,
                  width: 38,
                  height: 38,
                  borderRadius: 19,
                  backgroundColor: ACCENT,
                  alignItems: 'center',
                  justifyContent: 'center',
                }}>
                <Text style={{fontFamily: DISPLAY, fontSize: 28, color: F.brownDark}}>?</Text>
              </View>
            </View>
            {cover.task ? (
              <View style={{position: 'absolute', left: 34, top: 304, width: 232, alignItems: 'center'}}>
                <View
                  style={{
                    paddingVertical: 5,
                    paddingHorizontal: 12,
                    borderRadius: 8,
                    backgroundColor: 'rgba(0,0,0,0.65)',
                  }}>
                  <CCText style={{fontSize: 15, fontWeight: '700', color: F.white}}>{cover.task}</CCText>
                </View>
              </View>
            ) : null}
          </>
        ) : null}

        <View
          style={{
            position: 'absolute',
            left: hasBoard ? 300 : 34,
            top: 52,
            width: hasBoard ? 312 : 572,
            gap: 10,
          }}>
          <Text
            numberOfLines={3}
            style={{
              fontFamily: DISPLAY,
              fontSize: hasBoard ? 60 : 76,
              lineHeight: hasBoard ? 56 : 70,
              color: F.white,
              textTransform: 'uppercase',
              ...SHADOW,
            }}>
            {cover.title}
          </Text>
          {cover.hook ? (
            <Text
              numberOfLines={2}
              style={{
                fontFamily: DISPLAY,
                fontSize: 36,
                lineHeight: 36,
                color: ACCENT,
                textTransform: 'uppercase',
                ...SHADOW,
              }}>
              {cover.hook}
            </Text>
          ) : null}
          {cover.themes.length ? (
            <View style={{flexDirection: 'row', flexWrap: 'wrap', gap: 6}}>
              {cover.themes.map(theme => (
                <View
                  key={theme}
                  style={{
                    paddingVertical: 3,
                    paddingHorizontal: 9,
                    borderRadius: 6,
                    borderWidth: 1.5,
                    borderColor: ACCENT,
                    backgroundColor: 'rgba(0,0,0,0.45)',
                  }}>
                  <CCText style={{fontSize: 13, fontWeight: '700', color: F.white}}>{theme}</CCText>
                </View>
              ))}
            </View>
          ) : null}
        </View>

        {coach ? (
          <View
            style={{
              position: 'absolute',
              right: 24,
              bottom: 20,
              flexDirection: 'row',
              alignItems: 'center',
              gap: 10,
              paddingVertical: 6,
              paddingLeft: 6,
              paddingRight: 14,
              borderRadius: 40,
              backgroundColor: 'rgba(20,16,13,0.85)',
            }}>
            <View
              style={{
                width: PHOTO,
                height: PHOTO,
                borderRadius: PHOTO / 2,
                overflow: 'hidden',
                backgroundColor: '#3B312A',
                alignItems: 'center',
                justifyContent: 'center',
              }}>
              {photo ? (
                <img
                  src={coach.photo_url}
                  alt={coachLabel(coach)}
                  style={{position: 'absolute', ...photo}}
                />
              ) : (
                <Text style={{fontFamily: DISPLAY, fontSize: 28, color: F.white}}>
                  {initials(coach.name)}
                </Text>
              )}
              <View
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  borderRadius: PHOTO / 2,
                  borderWidth: 3,
                  borderColor: ACCENT,
                }}
              />
            </View>
            <View style={{gap: 3}}>
              {coach.title ? (
                <View
                  style={{
                    alignSelf: 'flex-start',
                    paddingVertical: 2,
                    paddingHorizontal: 6,
                    borderRadius: 5,
                    backgroundColor: ACCENT,
                  }}>
                  <CCText style={{fontSize: 12, fontWeight: '700', color: F.brownDark}}>{coach.title}</CCText>
                </View>
              ) : null}
              <CCText style={{fontSize: 16, fontWeight: '700', color: F.white}}>{coach.name}</CCText>
            </View>
          </View>
        ) : null}
      </View>
    </View>
  );
}
